import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small dialog anchored under an element, portaled to <body>.
 *
 * The design system has no popover — `Modal` blocks the page and `Select` only
 * carries a list of options, neither of which suits "collect one more value
 * before saving this row". A portal is what makes it usable inside the grants
 * table, which scrolls in both axes and would otherwise clip the panel.
 *
 * Mount it to open it and unmount it to close: there is no `isOpen` prop, so the
 * content's own state is seeded on mount rather than synced by an effect.
 */

const GAP = 4;
const VIEWPORT_PADDING = 8;

function position(anchor: HTMLElement, panel: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const { offsetWidth: width, offsetHeight: height } = panel;

  // Flip above the anchor when the panel would run off the bottom, and clamp to
  // the viewport so a cell at the right edge of a wide table stays reachable.
  const below = rect.bottom + GAP;
  const top =
    below + height > window.innerHeight - VIEWPORT_PADDING
      ? Math.max(VIEWPORT_PADDING, rect.top - GAP - height)
      : below;
  const left = Math.min(
    Math.max(VIEWPORT_PADDING, rect.left),
    Math.max(VIEWPORT_PADDING, window.innerWidth - width - VIEWPORT_PADDING),
  );

  panel.style.top = `${top}px`;
  panel.style.left = `${left}px`;
  panel.style.visibility = 'visible';
}

type AnchoredPopoverProperties = {
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  label: string;
  children: React.ReactNode;
};

export function AnchoredPopover({
  onClose,
  anchorRef,
  label,
  children,
}: AnchoredPopoverProperties) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Written straight to the DOM rather than held in state: this is measure-then-
  // place, so a render pass per reposition would buy nothing and the panel would
  // flash at its old offset while the page scrolls. A layout effect so the panel
  // is placed in the frame it is first painted.
  useLayoutEffect(() => {
    const reposition = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor && panel) {
        position(anchor, panel);
      }
    };
    reposition();
    // Capture phase: the anchor's scroll container is a nested div, and scroll
    // does not bubble. Recompute rather than close, so the panel tracks the row.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchorRef]);

  useEffect(() => {
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    // Clicks on the anchor are excluded so the control that opened the panel can
    // also close it, instead of closing and reopening in the same gesture.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !anchorRef.current?.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      // Hidden until measured, so the flip decision is made against the panel's
      // real height rather than zero.
      style={{ top: 0, left: 0, visibility: 'hidden' }}
      className="fixed z-50 rounded-lg border border-gray-200 bg-white p-3 shadow-lg outline-none dark:border-gray-700 dark:bg-gray-800"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
